import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';
import StatCard from './StatCard';

const PerformanceTab = ({ token }) => {
  const { showSuccess } = useNotifications();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    const fetchPerf = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/perf/report`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();

        if (result.success) {
          setData(result.data);
        }
      } catch (err) {
        console.error('Erreur chargement perf:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPerf();
  }, [token]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;
  }

  if (!data) {
    return (
      <div style={{
        background: '#fff3cd',
        padding: '24px',
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <p>Donnees de performance non disponibles. La Phase 3 doit etre deployee.</p>
      </div>
    );
  }

  const { summary, health, slowestEndpoints, recentSlowRequests, recentErrors } = data;

  return (
    <div>
      {/* Statut sante */}
      <div style={{
        padding: '16px 24px',
        borderRadius: '12px',
        marginBottom: '24px',
        background: health?.status === 'healthy' ? '#e8f5e9' :
                   health?.status === 'slow' ? '#fff3e0' : '#ffebee',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '32px' }}>
          {health?.status === 'healthy' ? '[OK]' : health?.status === 'slow' ? '[!]' : '[X]'}
        </span>
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            Statut : {health?.status === 'healthy' ? 'Sain' :
                     health?.status === 'slow' ? 'Ralenti' : 'Degrade'}
          </div>
          {health?.issues && health.issues.length > 0 && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              {health.issues.join(' - ')}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard icon="[T]" title="Uptime" value={summary?.uptime || '-'} color="#4caf50" />
        <StatCard icon="[R]" title="Requetes" value={summary?.totalRequests || 0} color="#2196f3" />
        <StatCard icon="[A]" title="Temps moyen" value={summary?.avgResponseTime || '-'} color="#ff9800" />
        <StatCard icon="[E]" title="Taux erreur" value={summary?.errorRate || '0%'} color="#f44336" />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px'
      }}>
        {/* Endpoints les plus lents */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Endpoints les plus lents</h3>
          {slowestEndpoints?.slice(0, 5).map((ep, idx) => (
            <div key={idx} style={{
              padding: '10px 0',
              borderBottom: idx < 4 ? '1px solid #eee' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <code style={{ fontSize: '12px', color: '#666' }}>{ep.endpoint}</code>
              <span style={{
                background: ep.avgDuration > 1000 ? '#ffebee' : '#fff3e0',
                color: ep.avgDuration > 1000 ? '#c62828' : '#f57c00',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {ep.avgDuration}ms
              </span>
            </div>
          )) || <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnee</p>}
        </div>

        {/* Erreurs recentes */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Erreurs recentes</h3>
          {recentErrors?.length > 0 ? recentErrors.slice(0, 10).map((err, idx) => (
            <div
              key={err.id || idx}
              onClick={() => setSelectedError(err)}
              style={{
                padding: '10px 0',
                borderBottom: idx < 9 ? '1px solid #eee' : 'none',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <code style={{ fontSize: '12px' }}>{err.method} {err.path}</code>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    background: '#ffebee',
                    color: '#c62828',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }}>
                    {err.statusCode}
                  </span>
                  <span style={{ fontSize: '11px', color: '#1976d2' }}>[Details]</span>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                {new Date(err.timestamp).toLocaleString('fr-FR')}
                {err.requestContext?.userName && ` - ${err.requestContext.userName}`}
              </div>
              {err.responseBody?.error && (
                <div style={{ fontSize: '11px', color: '#c62828', marginTop: '2px' }}>
                  {err.responseBody.error}
                </div>
              )}
            </div>
          )) : (
            <p style={{ color: '#666', textAlign: 'center' }}>Aucune erreur recente</p>
          )}
        </div>
      </div>

      {/* Modale detail erreur */}
      {selectedError && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedError(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#c62828' }}>
                Detail erreur {selectedError.statusCode}
              </h3>
              <button
                onClick={() => setSelectedError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                x
              </button>
            </div>

            {/* Infos principales */}
            <div style={{
              background: '#ffebee',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <div style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '600' }}>
                {selectedError.method} {selectedError.path}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                {new Date(selectedError.timestamp).toLocaleString('fr-FR')}
              </div>
            </div>

            {/* Contexte utilisateur */}
            {selectedError.requestContext && (
              <Section title="Utilisateur">
                <InfoRow label="User ID" value={selectedError.requestContext.userId || 'Non authentifie'} />
                <InfoRow label="Nom" value={selectedError.requestContext.userName || '-'} />
                <InfoRow label="Role" value={selectedError.requestContext.userRole || '-'} />
                <InfoRow label="IP" value={selectedError.requestContext.ip || selectedError.ip} />
                <InfoRow label="User Agent" value={selectedError.requestContext.userAgent || selectedError.userAgent} small />
              </Section>
            )}

            {/* Parametres de la requete */}
            {selectedError.requestContext && (
              <Section title="Requete">
                <InfoRow label="URL complete" value={selectedError.requestContext.originalUrl} />
                {Object.keys(selectedError.requestContext.params || {}).length > 0 && (
                  <InfoRow label="Params" value={JSON.stringify(selectedError.requestContext.params, null, 2)} code />
                )}
                {Object.keys(selectedError.requestContext.query || {}).length > 0 && (
                  <InfoRow label="Query" value={JSON.stringify(selectedError.requestContext.query, null, 2)} code />
                )}
                {selectedError.requestContext.body && (
                  <InfoRow label="Body" value={
                    typeof selectedError.requestContext.body === 'string'
                      ? selectedError.requestContext.body
                      : JSON.stringify(selectedError.requestContext.body, null, 2)
                  } code />
                )}
              </Section>
            )}

            {/* Reponse */}
            {selectedError.responseBody && (
              <Section title="Reponse serveur">
                <pre style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {JSON.stringify(selectedError.responseBody, null, 2)}
                </pre>
              </Section>
            )}

            {/* Stack trace si disponible */}
            {selectedError.error?.stack && (
              <Section title="Stack Trace">
                <pre style={{
                  background: '#263238',
                  color: '#80cbc4',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  overflow: 'auto',
                  maxHeight: '200px'
                }}>
                  {selectedError.error.stack}
                </pre>
              </Section>
            )}

            {/* Bouton copier pour debug */}
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(selectedError, null, 2));
                  showSuccess('Copié dans le presse-papier');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Copier JSON complet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composants utilitaires pour la modale
const Section = ({ title, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666', textTransform: 'uppercase' }}>
      {title}
    </h4>
    {children}
  </div>
);

const InfoRow = ({ label, value, code = false, small = false }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '8px' }}>
      <span style={{ fontSize: '12px', color: '#666' }}>{label}: </span>
      {code ? (
        <pre style={{
          background: '#f5f5f5',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '11px',
          overflow: 'auto',
          margin: '4px 0 0 0'
        }}>
          {value}
        </pre>
      ) : (
        <span style={{
          fontSize: small ? '11px' : '12px',
          color: '#333',
          wordBreak: 'break-all'
        }}>
          {value}
        </span>
      )}
    </div>
  );
};

export default PerformanceTab;
